/**
 * 产品管理 Hooks（领域/产品/版本，TanStack Query）
 *
 * 个人空间分组管理复用 project-management 模块的 useProjectGroups
 * （/project-groups 接口），本文件不再提供个人分组 CRUD hooks。
 */

import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import { productService } from '../services/productTreeService'
import type { VersionRow } from '../types'

export const productKeys = {
  all: ['product-mgmt'] as const,
  domains: () => [...productKeys.all, 'domains'] as const,
  sidebarGroups: () => [...productKeys.all, 'sidebar-groups'] as const,
  tree: (params: { group_id?: number | null }) =>
    [...productKeys.all, 'tree', params] as const,
  products: (domainId: number) => [...productKeys.all, 'products', domainId] as const,
  versions: (params: { page: number; page_size: number; domain_id?: number; keyword?: string }) =>
    [...productKeys.all, 'versions', params] as const,
  productVersions: (productPid: string) => [...productKeys.all, 'product-versions', productPid] as const,
}

export const useDomains = () =>
  useQuery({ queryKey: productKeys.domains(), queryFn: () => productService.listDomains(), staleTime: 5 * 60 * 1000 })

/** 侧边栏分组（领域 + 个人空间入口，show_in_sidebar=true） */
export const useSidebarGroups = () =>
  useQuery({ queryKey: productKeys.sidebarGroups(), queryFn: () => productService.listSidebarGroups(), staleTime: 5 * 60 * 1000 })

export const useCreateDomain = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (name: string) => productService.createDomain(name),
    onSuccess: () => qc.invalidateQueries({ queryKey: productKeys.domains() }),
  })
}

export const useProducts = (domainId: number | null) =>
  useQuery({
    queryKey: productKeys.products(domainId ?? 0),
    queryFn: () => productService.listProducts(domainId!),
    enabled: !!domainId,
    staleTime: 5 * 60 * 1000,
  })

/** 指定产品下的版本（level=2，parent_project_id=产品） */
export const useProductVersions = (productPid: string | null) =>
  useQuery({
    queryKey: productKeys.productVersions(productPid ?? ''),
    queryFn: () => productService.listVersionsByProduct(productPid!),
    enabled: !!productPid,
    staleTime: 5 * 60 * 1000,
  })

/**
 * 域下全部版本（level=2，经产品 group_id 关联到领域；下拉与"本级+下级"子树展开用）
 * 自动翻页取全量。仅当 enabled 且 domainId 存在时拉取。
 */
export const useDomainVersions = (domainId: number | null, enabled: boolean = true) =>
  useQuery<VersionRow[], Error>({
    queryKey: [...productKeys.all, 'domain-versions', domainId ?? 0],
    queryFn: async () => {
      const all: VersionRow[] = []
      let page = 1
      let total = Infinity
      while (all.length < total) {
        const res = await productService.listVersions({ page, page_size: 100, domain_id: domainId! })
        all.push(...res.items)
        total = res.total
        if (res.items.length === 0) break
        page++
      }
      return all
    },
    enabled: !!domainId && enabled,
    staleTime: 5 * 60 * 1000,
  })

export const useVersions = (params: { page: number; page_size: number; domain_id?: number; keyword?: string }) =>
  useQuery({
    queryKey: productKeys.versions(params),
    queryFn: () => productService.listVersions(params),
    placeholderData: keepPreviousData,
  })

export const useCreateVersion = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Parameters<typeof productService.createVersion>[0]) => productService.createVersion(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...productKeys.all, 'versions'] })
      qc.invalidateQueries({ queryKey: [...productKeys.all, 'products'] })
    },
  })
}

export const useUpdateVersion = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ versionPid, data }: { versionPid: string; data: Parameters<typeof productService.updateVersion>[1] }) =>
      productService.updateVersion(versionPid, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...productKeys.all, 'versions'] })
      qc.invalidateQueries({ queryKey: [...productKeys.all, 'products'] })
    },
  })
}

export const useDeleteVersion = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (versionPid: string) => productService.deleteVersion(versionPid),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...productKeys.all, 'versions'] }),
  })
}

export const useDeleteProduct = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (productPid: string) => productService.deleteProduct(productPid),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...productKeys.all, 'versions'] })
      qc.invalidateQueries({ queryKey: [...productKeys.all, 'products'] })
    },
  })
}

// ===== 目录树（领域/个人空间，完整嵌套树）=====

export const useFullTree = (params: { group_id?: number | null }) =>
  useQuery({
    queryKey: productKeys.tree(params),
    queryFn: () => productService.getFullTree(params),
    enabled: params.group_id != null && params.group_id > 0,
    staleTime: 30 * 1000,
  })

const invalidateTree = (qc: ReturnType<typeof useQueryClient>) =>
  qc.invalidateQueries({ queryKey: [...productKeys.all, 'tree'] })

/** 领域/个人空间下新建产品 */
export const useCreateProduct = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ domainId, name, description, webhook, git_config }: { domainId: number; name: string; description?: string; webhook?: any; git_config?: any }) =>
      productService.createProduct(domainId, name, description, webhook, git_config),
    onSuccess: () => invalidateTree(qc),
  })
}

/** 产品下新建版本 */
export const useCreateVersionUnderProduct = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ productPid, name, description, webhook, git_config }: { productPid: string; name: string; description?: string; webhook?: any; git_config?: any }) =>
      productService.createVersionUnderProduct(productPid, name, description, webhook, git_config),
    onSuccess: () => invalidateTree(qc),
  })
}

/** 版本/文件夹下新建子节点（复用 projects 接口） */
export const useCreateChildNode = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Parameters<typeof productService.createChildNode>[0]) =>
      productService.createChildNode(data),
    onSuccess: () => invalidateTree(qc),
  })
}

/** 删除任意节点（产品/版本/文件夹/项目，级联删子树） */
export const useDeleteNode = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (projectPid: string) => productService.deleteNode(projectPid),
    onSuccess: () => invalidateTree(qc),
  })
}
